import {IconDefinition} from "@fortawesome/fontawesome-svg-core";
import {faHeading, faParagraph, faImage, faImages} from "@fortawesome/pro-regular-svg-icons";

/**
 * Available block types for the email block editor.
 */
export type BlockType = 'heading' | 'paragraph' | 'imageFullWidth' | 'imageFullWidthWithOverlay';

/**
 * A single content block in an email template.
 */
export interface EmailBlock {
    /** Unique identifier for the block */
    id: string;
    /** The type of block (determines which template is used) */
    type: BlockType;
    /** The content of the block (may contain HTML for rich text) */
    content: string;
    /** Image identifier for image blocks */
    imageIdentifier?: string;
    /** Alt text for image blocks */
    altText?: string;
    /** Headline for imageFullWidthWithOverlay blocks */
    headline?: string;
    /** Sub-headline for imageFullWidthWithOverlay blocks */
    subHeadline?: string;
}

/**
 * Check if a block type is an image block
 */
export const isImageBlock = (type: BlockType): boolean => {
    return type === 'imageFullWidth' || type === 'imageFullWidthWithOverlay';
};

/**
 * Configuration for a block type shown in the palette.
 */
export interface BlockTypeConfig {
    type: BlockType;
    labelKey: string;
    icon: IconDefinition;
    defaultContent: string;
}

/**
 * All available block types with their configuration.
 */
export const BLOCK_TYPES: BlockTypeConfig[] = [
    {
        type: 'heading',
        labelKey: 'emailBlock.type.heading',
        icon: faHeading,
        defaultContent: '',
    },
    {
        type: 'paragraph',
        labelKey: 'emailBlock.type.paragraph',
        icon: faParagraph,
        defaultContent: '<p></p>',
    },
    {
        type: 'imageFullWidth',
        labelKey: 'emailBlock.type.imageFullWidth',
        icon: faImage,
        defaultContent: '',
    },
    {
        type: 'imageFullWidthWithOverlay',
        labelKey: 'emailBlock.type.imageFullWidthWithOverlay',
        icon: faImages,
        defaultContent: '',
    },
];

/**
 * Generate a unique block ID.
 */
export const generateBlockId = (): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = 'blk-';
    for (let i = 0; i < 8; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
};

/**
 * Parse blocks JSON string to array.
 * Returns empty array if parsing fails or content is not blocks JSON.
 */
export const parseBlocks = (jsonString: string): EmailBlock[] => {
    if (!jsonString || !jsonString.trim().startsWith('[')) {
        return [];
    }
    try {
        const parsed = JSON.parse(jsonString);
        if (Array.isArray(parsed)) {
            return parsed as EmailBlock[];
        }
    } catch {
        // Not valid JSON
    }
    return [];
};

/**
 * Stringify blocks array to JSON.
 */
export const stringifyBlocks = (blocks: EmailBlock[]): string => {
    return JSON.stringify(blocks);
};
